const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const visibleFocusable = (node: HTMLElement) =>
  Array.from(node.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) => element.getClientRects().length > 0 || element === document.activeElement
  );

export const trapDialogFocus = (node: HTMLElement) => {
  const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const previousTabIndex = node.getAttribute('tabindex');
  const appShell = document.querySelector<HTMLElement>('.app-shell');
  const canInertShell = Boolean(appShell && !appShell.contains(node));
  const previousInert = appShell?.hasAttribute('inert') ?? false;
  const previousAriaHidden = appShell?.getAttribute('aria-hidden');

  if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
  if (canInertShell) {
    appShell?.setAttribute('inert', '');
    appShell?.setAttribute('aria-hidden', 'true');
  }

  const focusInitial = () => {
    const target = node.querySelector<HTMLElement>('[data-autofocus]') ?? visibleFocusable(node)[0] ?? node;
    target.focus({ preventScroll: true });
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const focusable = visibleFocusable(node);

    if (!focusable.length) {
      event.preventDefault();
      node.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !node.contains(active))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  document.addEventListener('keydown', handleKeydown, true);
  requestAnimationFrame(focusInitial);

  return {
    destroy() {
      document.removeEventListener('keydown', handleKeydown, true);
      if (previousTabIndex == null) node.removeAttribute('tabindex');
      else node.setAttribute('tabindex', previousTabIndex);

      if (appShell && canInertShell) {
        if (!previousInert) appShell.removeAttribute('inert');
        if (previousAriaHidden == null) appShell.removeAttribute('aria-hidden');
        else appShell.setAttribute('aria-hidden', previousAriaHidden);
      }

      previousFocus?.focus({ preventScroll: true });
    }
  };
};
